import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, Subscription } from "rxjs";

import { ApiGatewayHelperService } from './../../app/services/apigateway-helper.service';
import { ImgBookService } from './img-book.service';
import { StudentBookService } from './student-book.service';
import { NetwordDetectionService } from 'modules/app/services/network-detection.service';
import { environment } from 'environments/environment';
import { DownloadHelperService } from 'modules/desktop/services/download-helper.service';
import { TabbingOrderService } from './tabbing-order.service';
import * as interact from 'interactjs/dist/interact.js';
import * as dragscroll from 'dragscroll/dragscroll';

declare var $:any;


@Injectable()
export class ImgBookHelperService {
    
    imgBook: any;
    imageMapBySerial : any;
    imageMapByLabel : any;
    defaultZoomConfig : any;
    isZoomHotspotCreated : boolean = false;
    firstPage: any;
    lastPage: any;
    units: any;
    overlayJson: any;
    currentBookCode: any;
	isDragEnabled = false; // variable to track if the current hotspot is in drag mode.
    disableDrag = false; // variable to track if drag mode is enabled and can hotspots be dragged


    // add the hotspot type and corresponding svg name to the map.
    // also prefetch the svg in student-book.
    hotspotToImgMap = {
        "audio" : "assets/img/Hotspots/audio_icon.svg",
        "audio-package" : "assets/img/Hotspots/audio_icon.svg",
        "video" : "assets/img/Hotspots/video_icon.svg",
        "video-package" : "assets/img/Hotspots/video_icon.svg",
        "url" : "assets/img/Hotspots/link_icon.svg",
        "page" : "assets/img/Hotspots/jump_page_icon.svg",
        "predefined-zoom" : "assets/img/Hotspots/zoom_icon.svg",
        "learning-object" : "assets/img/Hotspots/LO_icon.svg",
        "answer-key" : "assets/img/Hotspots/AK_icon.svg",
        "game" : "assets/img/Hotspots/game_icon.svg",
        "note" : "assets/img/Hotspots/note_icon.svg",
        "selected_note" : "assets/img/Hotspots/note_selected_icon.svg",
        "pdf" : "assets/img/Hotspots/link_icon.svg",
    }

    // flag to store whether the system is online or offline
    // needed when book is launched in desktop app
    isOnline: boolean = false;
    networkStatusSubscription: Subscription;

    private notePinPositionUpdateNotifier = new Subject<any>();
    notePinPositionUpdateObservable$ = this.notePinPositionUpdateNotifier.asObservable();

    constructor(private http: HttpClient, 
        private apiGatewayHelperService: ApiGatewayHelperService,
        private imgBookService: ImgBookService,
        private studentBookService: StudentBookService,
        private networkService: NetwordDetectionService,
        private downloadHelperService: DownloadHelperService,
        private readonly tabbingOrderService: TabbingOrderService
    ) {

        this.hotspotEventListener = this.hotspotEventListener.bind(this);
        if(environment.isDesktopApp) {
            this.networkService.isOnline(true).then(result => {
                this.isOnline = result;
                this.toggleLOHotspotsForNetwork(result);
            });
            this.networkStatusSubscription = this.networkService.monitorStatus().subscribe(async (isOnline) => {
                if (this.isOnline != isOnline) {
                    this.isOnline = isOnline;
                    this.toggleLOHotspotsForNetwork(isOnline);
                }
            });
        } else {
            this.isOnline = true;
        }

    }

    // get imgbook instance
    public getImgBook(options) {
        if(!this.imgBook) {
            this.imgBook  = this.imgBookService.getImgBook(options);
        }
        return this.imgBook;
    }

    public setCurrentBookCode(bookCode) {
        this.currentBookCode = bookCode;
    }

    public removeImgBookInstance() {

        this.imageMapBySerial = undefined;
        this.imageMapByLabel = undefined;
        this.defaultZoomConfig = undefined;
        this.firstPage = undefined;
        this.lastPage = undefined;
        this.units = undefined;
        this.overlayJson = undefined;
        this.currentBookCode = undefined;

        this.imgBook = undefined;
        this.imgBookService.removeImgBookInstance();
    }

    public getDefaultZoomConfig() {
        this.defaultZoomConfig =  this.defaultZoomConfig || this.imgBook.getZoomConfig();
        return JSON.parse(JSON.stringify(this.defaultZoomConfig));
    }

    public getUnitsInfo(studentBook, numberOfPages, currentPageLabel) {
        this.imageMapBySerial = this.imgBook.getImageMapByPageSerial(1, numberOfPages);
        let start = this.imageMapBySerial.pages["sno-to-label-map"][this.imageMapBySerial.pages["start-sno"]];
        let end = this.imageMapBySerial.pages["sno-to-label-map"][this.imageMapBySerial.pages["end-sno"]]; 
        this.imageMapByLabel = this.imgBook.getImageMapByPageLabel(start, end);

        let units = this.studentBookService.getUnits(studentBook, this.imageMapBySerial, this.imageMapByLabel);
        this.units = units;

        // page after cover page specific handling (since it is a lost page)
        if(units[0].items.length>1) {
            let secondPage = units[0].items[1];
            if(units[1] && units[1].items) {
                units[1].items.unshift(units[0].items[1]);
                units[1].start = units[1].items[0].serial;
                units[1]["start-label"] = units[1].items[0].label;
                units[1].items[0].name = units[1].items[1].name;
            }
            units[0].end = units[0].start;
            units[0]["end-label"] = units[0]["start-label"];
            units[0].items.splice(1,1);
        }

        let currentPageSerial = this.imageMapByLabel.pages["label-to-sno-map"][currentPageLabel];
        let currentUnit = units.find(unit => unit.start <= currentPageSerial && unit.end >= currentPageSerial);
        this.firstPage = units && units[0] && units[0].items && units[0].items[0];
        this.lastPage = units && units[units.length-1] && units[units.length-1].items && units[units.length-1].items[units[units.length-1].items.length-1];
        return {
            units: units,
            currentUnit: currentUnit
        }
    }

    // @zoomType can be "ZOOM_OUT"/"ZOOM_IN" (for now)
    public canZoom(zoomType) {
        if(zoomType == "ZOOM_IN") {
            return this.imgBook.canZoomIn();
        } else {
            if(this.isFitToScreen()) {
                return false;
            }
            return true;
        }
    }
    
    public isFitToWidth() {
        let width = $(".foc-container").width();
        // toc is open for devices whose toc overlay is relative (or toc position is right and width>=576)
        if(document.querySelector(".sidebar")) {
            if(window.getComputedStyle(document.querySelector(".sidebar")).position == "relative" ||
            (window.getComputedStyle(document.querySelector(".sidebar")).right == "0px" && $('.foc-container').width()>=576)) {
                width = width-320;
            }
        }

        // notes-wrapper is open for devices whose panel overlay is relative (or toc position is right and width>=576)
        // similar check as of toc.
        // #475 : Regression Issue - Fit to width with Notes Pane
        if(document.querySelector('.notes-wrapper')) {
            if(window.getComputedStyle(document.querySelector('.notes-wrapper')).position == "relative" ||
            (window.getComputedStyle(document.querySelector('.notes-wrapper')).right == "0px" && $('.foc-container').width()>=576)) {
                width = width-320;
            }
        }
        return this.imgBook.isContentFitToWidth(width);
    }

    public isFitToScreen() {
        let height = $(".foc-container").height();
        if(this.isTouchDevice()) {
            // For mobile devices (toolbar height needs to be subtracted to get actual height)
            if(document.querySelector(".mobile") && window.getComputedStyle(document.querySelector(".mobile")) && window.getComputedStyle(document.querySelector(".mobile")).display=="block") {
                height = height - parseInt(window.getComputedStyle(document.querySelector(".mobile")).height.replace("px",""));
            } 
        }
        // toolbar, if any, height needs to be subtracted to get actual height
        let desktopToolbar:any = document.querySelector('.desktop-parent-toolbar');
        if(desktopToolbar && window.getComputedStyle(desktopToolbar, null).getPropertyValue("display") == 'block') {    
            height = $(".foc-container").height() - desktopToolbar.offsetHeight;
        } 
        return this.imgBook.isContentFitToScreen(height);
    }

    public fitToScreen(layout, cookie?) {
        let height = $(".foc-container").height();
        let width = $(".foc-container").width();
        
        if(cookie) {
            height = $("foc").height();
            width = $("foc").width();
        }

        // if desktop toolbar is loaded then, subtract the height of the toolbar (64px) 
        // from the foc container to get the actual height in which the imgbook should fit.
        let desktopToolbar:any = document.querySelector('.desktop-parent-toolbar');
        if(desktopToolbar && window.getComputedStyle(desktopToolbar, null).getPropertyValue("display") == 'block') {
            height = $(".foc-container").height() - desktopToolbar.offsetHeight;
            if(cookie) {
                height = $("foc").height() - desktopToolbar.offsetHeight;
            }
        }

        this.updateZoomConfig({"height": height, "width": width, "layout" : layout});
        this.imgBook.resetZoom();                
    }

    public fitToWidth(layout) {
        let height = $(".foc-container").height();

        let desktopToolbar:any = document.querySelector('.desktop-parent-toolbar');
        if(desktopToolbar && window.getComputedStyle(desktopToolbar, null).getPropertyValue("display") == 'block') {    
            height = $(".foc-container").height() - desktopToolbar.offsetHeight;
        }
        this.updateZoomConfig({"height": height, "width": $(".foc-container").width(), "layout" : layout});
        this.imgBook.resetZoom();

        let containerWidth = $('.foc-container').width();
        
        // notes-wrapper is open for devices whose panel overlay is relative (or toc position is right and width>=576)
        // similar check as of toc.
        // #475 : Regression Issue - Fit to width with Notes Pane
        if(document.querySelector('.notes-wrapper')) {
            if(window.getComputedStyle(document.querySelector('.notes-wrapper')).position == "relative" || (window.getComputedStyle(document.querySelector('.notes-wrapper')).right == "0px" && $('.foc-container').width()>=576)) {
                containerWidth = containerWidth-320;
            }
        }

        // toc is open for devices whose toc overlay is relative (or toc position is right and width>=576)
        if(document.querySelector(".sidebar")) {
            if(window.getComputedStyle(document.querySelector(".sidebar")).position == "relative" ||
            (window.getComputedStyle(document.querySelector(".sidebar")).right == "0px" && $('.foc-container').width()>=576)) {
                containerWidth = containerWidth - 320;
            }
        }
        
        //Issue fixed for screen crash when resizing imagebook when sidebar is open (with NEMO-11207)
        let zoomInCount = 1;
        while( parseInt($('#reader-container-wrapper').width()) !=  parseInt(containerWidth)) {
            zoomInCount++;
            if(zoomInCount == 15) { break; }
            this.imgBook.zoomin();
        }

    }

    // update zoom config based on supplied container width, height
    // resetZoom function needs to be called after this to reflect the applied changes
    public updateZoomConfig(options) {
        let pageDimension = this.imgBook.getPageDimensionsFromJSON();
        let pageHeight = pageDimension.height;
        let pageWidth = pageDimension.width; 

        let containerHeight = options.height;
        // For mobile devices (toolbar height needs to be subtracted to get actual height)
        if(document.querySelector(".mobile") && window.getComputedStyle(document.querySelector(".mobile")) && window.getComputedStyle(document.querySelector(".mobile")).display=="block") {
            containerHeight = containerHeight - parseInt(window.getComputedStyle(document.querySelector(".mobile")).height.replace("px",""));
        } 
        containerHeight = Math.floor(parseFloat(containerHeight));
        let containerWidth = options.width;
        

        // notes-wrapper is open for devices whose panel overlay is relative (or toc position is right and width>=576)
        // similar check as of toc.
        // #475 : Regression Issue - Fit to width with Notes Pane
        if (document.querySelector('.notes-wrapper')) {
            if (window.getComputedStyle(document.querySelector('.notes-wrapper')).position == "relative" ||
                (window.getComputedStyle(document.querySelector('.notes-wrapper')).right == "0px" && $('.foc-container').width() >= 576)) {
                containerWidth = containerWidth - 320;
            }
        }

        // toc is open for devices whose toc overlay is relative (or toc position is right and width>=576)
        if (document.querySelector(".sidebar")) {
            if (window.getComputedStyle(document.querySelector(".sidebar")).position == "relative" ||
                (window.getComputedStyle(document.querySelector(".sidebar")).right == "0px" && $('.foc-container').width() >= 576)) {
                containerWidth = containerWidth - 320;
            }
        }

        let aspectRatio = pageHeight/pageWidth;
        let finalWidth;

        var isCoverPage = (this.imgBook.getPageSerial() == 0) ? true : false;
        var isLastPage = (this.imgBook.getPageSerial() == (this.lastPage && this.lastPage.serial-1)) ? true : false;


        if(containerWidth < 768 || isCoverPage || isLastPage) {
            options.layout = "single";
        } else {
            options.layout = options.layout || (containerWidth > 768 ? "double" : "single");
        }

        finalWidth = options.layout=="double" ? 2*containerHeight/aspectRatio: containerHeight/aspectRatio;

        finalWidth = finalWidth > containerWidth ? containerWidth : finalWidth;
        
        let finalWidthBoolean = false;
        let containerWidthBoolean = false;
        let zoomConfig = this.getDefaultZoomConfig();

        let bestZoomStateIndex;
        
        zoomConfig.forEach(function(zoomState, i) { 
            if(zoomState.pcwidth >= finalWidth) {
                if(!finalWidthBoolean) {
                    finalWidthBoolean = true;
                    zoomConfig[i].pcwidth = Math.floor(parseFloat(finalWidth));
                    bestZoomStateIndex = i;
                }

                if(containerWidth > 768) {
                    zoomConfig[i].singlePage = "false";
                }
            }            
        });

        if(finalWidth != containerWidth) {

            zoomConfig.forEach(function(zoomState, i) { 
                
                if(zoomState.pcwidth >= containerWidth) {
                    if(!containerWidthBoolean) {
                        containerWidthBoolean = true;
                        zoomConfig[i].pcwidth = Math.floor(parseFloat(containerWidth));
                    }
                }            
            });    
        }

        // adding extra zoom states for double page layout so that Zoom In operations of Double Page are 
        // similar to single page zoom in operations
        if(options.layout == "double") {
            let extraZoomStates = [];
            zoomConfig.forEach(function(zoomState, i) {
                if(zoomConfig[zoomConfig.length-1].pcwidth < zoomState.pcwidth*2) {
                    extraZoomStates.push({
                        "pcwidth": zoomState.pcwidth*2,
                        "singlePage": "false",
                        "singlePageFitFactor": 1
                    });
                }
            });
            zoomConfig = zoomConfig.concat(extraZoomStates);
        }

        this.imgBook.setZoomConfig(zoomConfig, bestZoomStateIndex);
        
        return bestZoomStateIndex;
    }

    // returns true if device is touch screen, otherwise false
    public isTouchDevice() {
        // update the defination of touch devices by including ipad 13 and 14.
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent) || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    }
    
    // get current pages of imgbook
    public getCurrentPage(unit, units, operation, pageLabel) {
        const currentPagesLabels = this.imgBook.getPageNumber(); // array
        const currentPagesSerials = currentPagesLabels.map(label => this.imageMapByLabel.pages["label-to-sno-map"][label]);
        const pageSerialsBelongingToCurrentUnit = currentPagesSerials.filter(serial => unit.start <= serial && unit.end >= serial);
        let pages = []; 

        if(pageSerialsBelongingToCurrentUnit.length === currentPagesSerials.length) { // all pages belong to same unit
            pages = currentPagesSerials.map(serial => unit.items.find(item => item.serial === serial));
        } else {
            let pagesUnits = []; // keeping an array in case the previous or next unit contain only 1 page hence for 2 page navigation this will be an array
            units.forEach(unit => {
                currentPagesSerials.forEach(serial => {
                    if(unit.start <= serial && unit.end >= serial) {
                        const page = unit.items.find(item => item.serial === serial);
                        if(page) {
                            pagesUnits.push(unit);
                            pages.push(page);
                        }
                    }
                })
            });

            if(pageLabel) {
                pages.forEach((page, index) => unit = page.label == pageLabel ? pagesUnits[index] : unit);
            } else {
                if (pageSerialsBelongingToCurrentUnit.length) { 
                    if(unit.hide && pages.length > 1) {
                        const visibleUnitIndex = pagesUnits.findIndex(({hide}) => !hide);
                        if(visibleUnitIndex !== -1) {
                            unit = pagesUnits[visibleUnitIndex];
                            pages.splice(0, visibleUnitIndex);
                        }
                    }
                } else { // no page belong to current unit
                    if(pagesUnits.length > 1 && operation === "prev") unit = pagesUnits[pagesUnits.length - 1];
                    else unit = pagesUnits[0];
                }
            }
        } 
        return {pages, unit};
    }

    // get current page and currentUnit based on opened page (for now handling done for single page ONLY)
    public getCurrentPageInfo(units) {
        let currentUnit = undefined;
        let page = undefined;
        let currentPagesLabel = this.imgBook.getPageNumber();
        if(currentPagesLabel.length == 1) {
            units.forEach((unit)=> {
                if(unit.items) {
                    let items = unit.items;
                    items.forEach((item) => {
                        if(item.label == currentPagesLabel[0]) {
                            page = [];
                            page.push(item);
                            currentUnit = unit;
                        }
                    });
                }    
            });
        }
        if(currentUnit && page) {
            return { 
                unit: currentUnit, 
                page: page 
            };
        }
    }

    public getPageFromLabel(units, label) {
        let currentUnit = undefined;
        let page = undefined;
        let currentPageLabel = label;
        units.forEach((unit)=> {
            if(unit.items) {
                let items = unit.items;
                items.forEach((item) => {
                    if(item.label == currentPageLabel) {
                        page = [];
                        page.push(item);
                        currentUnit = unit;
                    }
                });
            }    
        });
        
        if(currentUnit && page) {
            return { 
                unit: currentUnit, 
                page: page 
            };
        }
    }

    // check if passed page is included in currentPage (used for highlighting active pages in TOC - Lesson View)
    public isActivePage(currentPage, label) {
        var matched = false;
        currentPage.forEach(function(page) {
            if(page.label == label) {
                matched = true;
            }
        });
        return matched;
    }

    // opens list view depending upon params (LESSON/UNITS) (default is LESSON)
    public showListView(options?) {
        options = options || {};
        
        if(options.unitsArray) {
            options.unitsArray.forEach(function(unit) {
                if(unit && unit['id']) {
                    $("#collapse-" + unit['id']).addClass("show");
                }
            });
        }
        
        let height = $(".app-container").height() - $(".sidebar-header-container").height();

        //NEMO-11207 spread toolbar under TOC
        if(window.innerWidth >= 576){
            height = height - 64;
        }
        
        // NEMO-5640: [Regression] | [eReader] Menu Bar not fully visible when cookies-banner is displayed and Table of content is open
        let cookiesBanner:any = document.querySelector('.cookies-banner');
        if(cookiesBanner) {
            let cookiesBannerHeight = cookiesBanner.offsetHeight;
            height -= cookiesBannerHeight;
        }

        if(window.innerWidth>768){ //below 200% zoom
            $(".sidebar").css('height','');
            $(".unit-list-scrollbar").height(height);

        }
        else if(window.innerWidth>426){ // below 300% zoom 
            $(".unit-list-scrollbar").css('height','');
            $(".sidebar").height($(".app-container").height() - $("#toolbar-wrapper-id").outerHeight());
        }
        else{ // above 300% zoom
            $(".unit-list-scrollbar").css('height','');
            $(".sidebar").height($(".app-container").height());
        }
        
       
        
        // if window is resized
        if(options.noScroll) {
            return;
        }

        // automatic scroll to active row
        let scrollTop = 0;
        if($(".unit-list-scrollbar").scrollTop()) { 
            // handling for next -prev navigation
            scrollTop += $(".unit-list-scrollbar").scrollTop();
        }
        if($(".page-details > .active").offset()) {
            scrollTop += $(".page-details > .active").offset().top-$(".unit-list-scrollbar").offset().top;
            scrollTop -= $(".page-details > .active").prev() ? 98 : 0;
            if(options.noAnimation) {
                $(".unit-list-scrollbar").scrollTop(scrollTop);
                return;
            }

            $(".unit-list-scrollbar").animate({ scrollTop: scrollTop}, 700);
            $(".unit-list-scrollbar").bind("touchstart mousewheel", function(e) {
                $(".unit-list-scrollbar").stop().unbind('scroll');
            });
        }
    }

    // opens list view 
    public showNoteListView(options?) {
        options = options || {};
        let pages = options.currentPage;
        let noteClass = options.noteClass;
        
        pages.forEach((page) => {
            if(!$("#collapse-" + page.label).hasClass("show"))
                $("#collapse-" + page.label).addClass("show");
        });

        let height = $(".app-container").height() - $(".notes-wrapper").find(".header-container").outerHeight()-$(".notes-wrapper").find(".footer").outerHeight();

        //NEMO-11207: Allow menu toolbar to spread under Notes
        if(window.innerWidth >= 576){
            height = height - 64;
        }
        // NEMO-7429 hack for
        // NEMO-7676 : [eReader] Difficulty in editing a note on mobile device landscape
        if (window.navigator && window.navigator.userAgent) {
            let agent = window.navigator.userAgent;
            let start = agent.indexOf("OS");
            let version;

            if (agent.indexOf("iPhone") > -1 && start > -1) {
                version = agent.substr(start + 3, 3).replace("_", ".");
                if (version.indexOf("13") == 0) {
                    console.log('special if check');
                    height = window.innerHeight - $(".notes-wrapper").find(".header-container").outerHeight()-$(".notes-wrapper").find(".footer").outerHeight();
                }
            }

        }

        
        // NEMO-5640: [Regression] | [eReader] Menu Bar not fully visible when cookies-banner is displayed and Table of content is open
        let cookiesBanner:any = document.querySelector('.cookies-banner');
        if(cookiesBanner) {
            let cookiesBannerHeight = cookiesBanner.offsetHeight;
            height -= cookiesBannerHeight;
        }
        
        let notesParentContainerHeight = $(".app-container").height() - $(".notes-wrapper .footer").outerHeight() - $("#toolbar-wrapper-id").outerHeight();
        if(window.innerWidth>768){
            $(".notes-container").height(height);
            $(".notes-parent-container").css('height','');
        } else if(window.innerWidth>426){
            $(".notes-container").css('height','');
            $(".notes-parent-container").height(notesParentContainerHeight);

        }
        else{
            $(".notes-container").css('height','');
            $(".notes-parent-container").height(notesParentContainerHeight+ $("#toolbar-wrapper-id").outerHeight());
        }

        
     
      
        // window is resized -> no scroll
        if(options.noScroll)
            return;

        // automatic scroll to active row
        // added if check to prevent crash on first time load when pages are null
        let scrollTop = 0;
        if($(".notes-container").scrollTop()) { 
            // handling for next -prev navigation
            scrollTop += $(".notes-container").scrollTop();
        }
        
        let activeElement = noteClass==".first-note" ? $("#"+pages[0].label) : $(".notes-container").find(".active").find(noteClass);
        if(activeElement.offset()) {
            scrollTop += activeElement.offset().top-$(".notes-container").offset().top;
            scrollTop -= activeElement.prev() ? 72 : 0;
            if(options.noAnimation) {
                $(".notes-container").scrollTop(scrollTop);
                return;
            }

            $(".notes-container").animate({ scrollTop: scrollTop}, 700);
            $(".notes-container").bind("touchstart mousewheel", function(e) {
                $(".notes-container").stop().unbind('scroll');
            });
        }      
    }


    public showLoader(loaderId?:string) {
        
        if(loaderId) {
            $("#" + loaderId).css("display", "flex");
        } else {
            $(".imgbook-loader").css("display", "flex");
        }
    }

    public hideLoader(loaderId?:string) {
        if(loaderId) {
            $("#" + loaderId).css("display", "none");
        } else {
            $(".imgbook-loader").css("display", "none");
        }
    }
    // public showFailed(loaderId?:string) {
        
    //     if(loaderId) {
    //         $("#" + loaderId).css("display", "flex");
    //     } else {
    //         $(".imgbook-loader").css("display", "flex");
    //     }
    // }
    // public hideFailed(loaderId?:string) {
    //     if(loaderId) {
    //         $("#" + loaderId).css("display", "none");
    //     } else {
    //         $(".imgbook-loader").css("display", "none");
    //     }
    // }

    // get current page layout (@return - 'single/double')
    public getCurrentPageLayout() {
        return this.imgBookService.getCurrentPageLayout();
    }

    // change page layout
    public changeImgbookLayout():void {
        let currentPages = this.imgBook.getPageNumber();
        let changeLayout;
        if(currentPages.length == 1) {
            changeLayout = PAGE_LAYOUT.DOUBLE;
        } else {
            changeLayout = PAGE_LAYOUT.SINGLE;            
        }
        this.imgBookService.changeImgbookLayout(changeLayout); 
    }
    
    public isToggleLayoutEnabled(units, unit, currentPage) {
        let enabled = true;
        let currentLayout = this.getCurrentPageLayout();
        
        // if double page is allowed or not (disabled for small screen)
        if(!this.imgBook.isDoublePageAllowed()) {
            enabled = false;
        }

        if(currentLayout == PAGE_LAYOUT.SINGLE) {    
            if(unit.label == "Cover" && currentPage[0].serial == 1) { // 1st page
                enabled = false;
            } else if(unit.label == units[units.length-1].label){ // last page
                let lastPage = unit.items[unit.items.length-1];
                if(lastPage.serial == currentPage[0].serial && lastPage.serial%2 == 0) { // if last page is even, double page toggle is disabled
                    enabled = false;
                }
            }
        }

        return enabled;
    }

    public hideHotspot(options?) {
        
        let pagesDIV;
        if(this.imgBook.getPageNumber().length == 2) {
            pagesDIV = ["A", "B"];
        } else {
            pagesDIV =  ["A"];
        }

        if(options && options.pageDIV) {
            if(Array.isArray(options.pageDIV)){
                pagesDIV = options.pageDIV;
            }else{
                pagesDIV = [options.pageDIV];
            }
        }

        setTimeout(function() {
            
            pagesDIV.forEach(function(pageDIV) {
                let overlayLayer, layerClone:any;
                
                if(document.querySelector('#ee-overlays'+ pageDIV) && document.querySelector('#ee-overlays'+ pageDIV).childNodes.length) {

                    if (document.querySelector('#ee-overlays-clone' + pageDIV)) {
                        document.querySelector('#ee-overlays-clone' + pageDIV).remove();
                    }
                    
                    overlayLayer = document.querySelector('#ee-overlays'+pageDIV);
                    layerClone = overlayLayer.cloneNode(true);
                    layerClone.id = 'ee-overlays-clone' + pageDIV;
                    overlayLayer.parentNode.appendChild(layerClone);
                    layerClone.style.zIndex = '10';
                    layerClone.style.pointerEvents = 'none';
                } else {

                    if (document.querySelector('#ee-overlays-clone' + pageDIV)) {
                        document.querySelector('#ee-overlays-clone' + pageDIV).remove();
                    }
                    
                }

                if(overlayLayer && layerClone) {
                    if(options.feature == "hideAll"){
                        overlayLayer.style.display = 'none';
                        layerClone.style.display = 'none';
                        return;
                    } else {
                        overlayLayer.style.display = 'block';
                        layerClone.style.display = 'block';
                    }
                }

                let x = options && options.x/parseFloat(document.getElementById("imgbook").style["font-size"].substring(0, document.getElementById("imgbook").style["font-size"].length-2));
                let y = options && options.y/parseFloat(document.getElementById("imgbook").style["font-size"].substring(0, document.getElementById("imgbook").style["font-size"].length-2));
                let w = options && options.w/parseFloat(document.getElementById("imgbook").style["font-size"].substring(0, document.getElementById("imgbook").style["font-size"].length-2));
                let h = options && options.h/parseFloat(document.getElementById("imgbook").style["font-size"].substring(0, document.getElementById("imgbook").style["font-size"].length-2));

                let hotspotLayer:any = document.querySelector('#ee-overlays'+ pageDIV).childNodes;
                if(hotspotLayer && hotspotLayer.length) {
                    for(let i=0; i<hotspotLayer.length;i++) {
                        let hotspot:any = {};
                        hotspot = hotspotLayer[i];
                        if(hotspot.nodeName == "DIV") {
                            let hotspotX = parseFloat(hotspot.style.left.substring(0, hotspot.style.left.length-2));
                            let hotspotY = parseFloat(hotspot.style.top.substring(0, hotspot.style.top.length-2));
                            let hotspotW = parseFloat(hotspot.childNodes[0].childNodes[0].style.width.substring(0, hotspot.childNodes[0].childNodes[0].style.width.length-2));
                            let hotspotH = parseFloat(hotspot.childNodes[0].childNodes[0].style.height.substring(0, hotspot.childNodes[0].childNodes[0].style.height.length-2));    
                            
                            let clonedHotspot:any = document.querySelector('#ee-overlays-clone'+ pageDIV).childNodes[i];
                            clonedHotspot.id = 'clonedHotspot-' + hotspot.id.split('-')[2];
                            clonedHotspot.classList.add('clonedHotspot');

                            let hotSpotTypes = ['audio', 'audio-package', 'video', 'video-package', 'url', 'page', 'learning-object', 'predefined-zoom', 'note', 'pdf'];

                            let isHotspotVisible = false;

                            hotSpotTypes.forEach(hotspotType => {
                                let tempHotspot = hotspot.querySelector("[type='" + hotspotType+ "']");
                                if(tempHotspot && tempHotspot.getAttribute('visibility') == "true") {
                                    isHotspotVisible = true;
                                }
                            })


                            if(isHotspotVisible) {
                                if(options && options.w) {
                                    let totalInside = false, partialInside = true;
                                    if(hotspotX>=x && (hotspotX+hotspotW)<=(x+w) && hotspotY>=y && (hotspotY+hotspotH)<=(y+h)) {
                                        totalInside = true;
                                    }

                                    if((hotspotX+hotspotW)<x || hotspotX>(x+w) || (hotspotY+hotspotH)<y || hotspotY>(y+h)) {
                                        partialInside = false;
                                    }                  
                                    
                                    if(options.feature == "spotlight" || options.feature =="predefined-zoom") { 
                                        if(totalInside) {
                                            // hotspot.style.display = "block";
                                            // clonedHotspot.style.display = "block";
                                            hotspot.classList.remove('hide-hotspot-for-selectionbox');
                                            clonedHotspot.classList.remove('hide-hotspot-for-selectionbox');      
                                            
                                            
                                            if(options.feature == "predefined-zoom") {
                                                if(hotspot.id == options.overlaysId) {
                                                    // hotspot.style.display = "none";
                                                    // clonedHotspot.style.display = "none";  
                                                    hotspot.classList.add('hide-hotspot-for-selectionbox');      
                                                    clonedHotspot.classList.add('hide-hotspot-for-selectionbox');      
                                                }
                                            }
                                        } else {
                                            // hotspot.style.display = "none";
                                            // clonedHotspot.style.display = "none";  
                                            hotspot.classList.add('hide-hotspot-for-selectionbox');      
                                            clonedHotspot.classList.add('hide-hotspot-for-selectionbox');   
                                        }
                                        

                                    } else if(options.feature == "mask"){
                                        let maskLayer:any = document.querySelector('#mask-layer-'+pageDIV);
                                        maskLayer.childNodes.forEach((maskDiv)=> {

                                            let x1 = maskDiv.offsetLeft/parseFloat(document.getElementById("imgbook").style["font-size"].substring(0, document.getElementById("imgbook").style["font-size"].length-2));
                                            let y1 = maskDiv.offsetTop/parseFloat(document.getElementById("imgbook").style["font-size"].substring(0, document.getElementById("imgbook").style["font-size"].length-2));
                                            let w1 = maskDiv.offsetWidth/parseFloat(document.getElementById("imgbook").style["font-size"].substring(0, document.getElementById("imgbook").style["font-size"].length-2));
                                            let h1 = maskDiv.offsetHeight/parseFloat(document.getElementById("imgbook").style["font-size"].substring(0, document.getElementById("imgbook").style["font-size"].length-2));
                                            
                                            if(hotspotX>=x1 && (hotspotX+hotspotW)<=(x1+w1) && hotspotY>=y1 && (hotspotY+hotspotH)<=(y1+h1)) {
                                                totalInside = true;
                                            }
        
                                            if((hotspotX+hotspotW)>x1 && hotspotX<(x1+w1) && (hotspotY+hotspotH)>y1 && hotspotY<(y1+h1)) {
                                                partialInside = true;
                                            }

                                            if(partialInside || totalInside) {
                                                // hotspot.style.display = "none";
                                                // clonedHotspot.style.display = "none";    
                                                hotspot.classList.add('hide-hotspot-for-selectionbox');      
                                                clonedHotspot.classList.add('hide-hotspot-for-selectionbox'); 
                                            } else {
                                                // hotspot.style.display = "block";
                                                // clonedHotspot.style.display = "block";   
                                                hotspot.classList.remove('hide-hotspot-for-selectionbox');
                                                clonedHotspot.classList.remove('hide-hotspot-for-selectionbox');  
                                            }

                                        });
                                    }
                                    
                                } else {
                                    
                                    // hotspot.style.display = "none";
                                    // clonedHotspot.style.display = "none";
                                    hotspot.classList.add('hide-hotspot-for-selectionbox');      
                                    clonedHotspot.classList.add('hide-hotspot-for-selectionbox'); 
                                }
                            }

                        }
                    }
                }
            });
        });
        
    }

    public showHotspot(options?) {
        
        let pagesDIV, that = this;
        // let t = options && (options.pageLoad || options.overlayJSON || options.toggleLayout) ? 500 : 0;
        setTimeout(function() {
            if(!that.imgBook) return;

            let isCurrentBookDownloaded = that.downloadHelperService.isBookDownloaded(that.currentBookCode);

            if(that.imgBook.getPageNumber().length == 2) {
                pagesDIV = ["A", "B"];
            } else {
                pagesDIV =  ["A"];
            }

            if(options && options.pageDIV) {
                pagesDIV = [options.pageDIV];
            }

            // show hotspots based on visibility
            let hotSpotTypes = ['audio', 'audio-package', 'video', 'video-package', 'url', 'page', 'predefined-zoom', 'learning-object', 'note', 'pdf'];
            hotSpotTypes.forEach(hotspotType => {
                let iconDiv = options && options.pageDIV ? document.getElementById("readerpagediv" + options.pageDIV).querySelectorAll('[type=' + hotspotType + ']') : document.querySelectorAll('[type=' + hotspotType + ']');
                Array.from(iconDiv).forEach((div) => {
                    if (div.getAttribute('type') == "note") {
                        div.setAttribute('tabIndex', (that.tabbingOrderService?.flattenedTabOrderMap?.notesPin?.currentIndex)?.toString());
                    }
                    else {
                        div.setAttribute('tabIndex', (that.tabbingOrderService?.flattenedTabOrderMap?.hotspots?.currentIndex)?.toString());
                    }
                    if (div.getAttribute('visibility') != "true") {
                        return;
                    }
                    let hotspotBox = div.childNodes[0];
                    if (!hotspotBox.childNodes || !hotspotBox.childNodes.length) {
                        let elem = document.createElement("img");
                        hotspotBox.appendChild(elem);

                        if (hotspotType == 'learning-object') {
                            elem.setAttribute("src", that.hotspotToImgMap[div.getAttribute('iconType') || 'answer-key']);
                        } else if (hotspotType == 'note') {

                            let selectedElem = document.createElement("img");
                            
                            elem.setAttribute("src", that.hotspotToImgMap[hotspotType]);
                            elem.setAttribute("class", "default-icon");

                            selectedElem.setAttribute("src", that.hotspotToImgMap['selected_note']);
                            selectedElem.setAttribute("class", "selected-icon");

                            hotspotBox.appendChild(selectedElem);
                        } else {
                            elem.setAttribute("src", that.hotspotToImgMap[hotspotType]);
                        }
                    }
                });
            });

            pagesDIV.forEach(function(pageDIV) {
                
                let overlayLayer, layerClone:any;          
                
                if(document.querySelector('#ee-overlays'+ pageDIV) && document.querySelector('#ee-overlays'+ pageDIV).childNodes.length) {

                    if (document.querySelector('#ee-overlays-clone' + pageDIV)) {
                        document.querySelector('#ee-overlays-clone' + pageDIV).remove();
                    }

                    overlayLayer = document.querySelector('#ee-overlays'+pageDIV);
                    layerClone = overlayLayer.cloneNode(true);
                    layerClone.id = 'ee-overlays-clone' + pageDIV;
                    overlayLayer.parentNode.appendChild(layerClone);
                    layerClone.style.zIndex = '10';
                    layerClone.style.pointerEvents = 'none';
                } else {

                    if (document.querySelector('#ee-overlays-clone' + pageDIV)) {
                        document.querySelector('#ee-overlays-clone' + pageDIV).remove();
                    }
                    
                }

                if(overlayLayer && layerClone) {
                    overlayLayer.style.display = 'block';
                    layerClone.style.display = 'block';
                }

                let hotspotLayerChildren = document.querySelector('#ee-overlays'+ pageDIV) && document.querySelector('#ee-overlays'+ pageDIV).childNodes;
                if(hotspotLayerChildren && hotspotLayerChildren.length) {
                    for(let i=0; i<hotspotLayerChildren.length;i++) {
                        let hotspot:any = {};
                        hotspot = hotspotLayerChildren[i];

                        if(!hotspot.id) continue;

                        let clonedHotspot:any = document.querySelector('#ee-overlays-clone'+ pageDIV).childNodes[i];
                        clonedHotspot.id = 'clonedHotspot-' + hotspot.id.split('-')[2];
                        clonedHotspot.classList.add('clonedHotspot');
                        clonedHotspot.childNodes[0].removeAttribute('tabIndex');

                        if(hotspot.nodeName == "DIV") {
                            // hotspot.style.display = "block";
                            // clonedHotspot.style.display = "block";

                            if(hotspot.querySelector("[type='learning-object']")){
                            
                                if(that.isOnline || isCurrentBookDownloaded){
                                    that.showLOHotspot(hotspot);
                                    that.showLOHotspot(clonedHotspot);
                                }else{
                                    that.hideLOHotspot(hotspot);
                                    that.hideLOHotspot(clonedHotspot);
                                }
                            }

                            hotspot.classList.remove('hide-hotspot-for-selectionbox');      
                            clonedHotspot.classList.remove('hide-hotspot-for-selectionbox'); 
                        }
                        
                    }
                }
            });

            that.addHotspotEventListeners();

        });
        
    }

    public toggleLOHotspotsForNetwork(state){
        if(state == undefined || !this.imgBook){
            return;
        }

        // if the book is downloaded, do not hide LOs
        let isCurrentBookDownloaded = this.downloadHelperService.isBookDownloaded(this.currentBookCode);

        let self = this;

        setTimeout(() => {
            let pagesDIV;
            if (self.imgBook && (self.imgBook.getPageNumber().length == 2)) {
                pagesDIV = ["A", "B"];
            } else {
                pagesDIV = ["A"];
            }
            pagesDIV.forEach(function(pageDIV) {
                let overlayLayer, layerClone:any;
    
                overlayLayer = document.querySelector('#ee-overlays'+ pageDIV);
                layerClone = document.querySelector('#ee-overlays-clone'+ pageDIV);
                
                let hotspotLayer:any = overlayLayer && overlayLayer.childNodes;
                let clonedHotspots:any = layerClone && layerClone.childNodes;

                if(hotspotLayer && hotspotLayer.length) {
                    for(let i=0; i<hotspotLayer.length;i++) {
                        let hotspot:any = {};
                        hotspot = hotspotLayer[i];
                        
                        if(hotspot.nodeName == "DIV" && hotspot.querySelector("[type='learning-object']")){
                            
                            if(state || isCurrentBookDownloaded){
                                self.showLOHotspot(hotspot);
                                clonedHotspots && clonedHotspots[i] && self.showLOHotspot(clonedHotspots[i]);
                            }else{
                                self.hideLOHotspot(hotspot);
                                clonedHotspots && clonedHotspots[i] && self.hideLOHotspot(clonedHotspots[i]);
                            }
                        }
                    }
                }
            });
            this.removeHotspotEventListeners();
        })

    }

    private hideLOHotspot(hotspot){
        // hotspot && (hotspot.style.display = "none");
        hotspot && (hotspot.classList.add('hide-hotspot-for-offline'));
    }

    private showLOHotspot(hotspot){
        // hotspot && (hotspot.style.display = "block");
        hotspot && (hotspot.classList.remove('hide-hotspot-for-offline'));
    }

    public getPageLabelByPageSerial(pageSerial) {
        return this.imageMapBySerial.pages["sno-to-label-map"][+pageSerial];
    }

    public getPageSerialByPageLabel(pageLabel) {
        return this.imageMapByLabel.pages["label-to-sno-map"][pageLabel];
    }

    public isPageLabelValid(pageLabel) {
        return this.imageMapByLabel.pages['label-to-sno-map'].hasOwnProperty(pageLabel);
    }

    public getUnits() {
        return this.units;
    }

    public setOverlayJSON(json) {
        this.overlayJson = json;
    }

    public getOverlayJSON() {
        return this.overlayJson;
    }

    /**
     * function to add different event listeners on hotspot icons.
     * for all hotspot types, add a mouseenter event listener that controls the animation of the hotspot by adding and removing classes
     * for note type hotspot, add interact.js hold event listener along with interact js drag listeners to control when and how to drag the note type hotspots.
     */
    public addHotspotEventListeners() {

        // get all visible hotspots
        let hotspots = document.querySelectorAll('.customVisibleClass');

        // add mouseenter event listener for each visible hotspot that is not cloned.
        Array.from(hotspots).forEach(hotspot => {
            hotspot.addEventListener('mouseenter', this.hotspotEventListener);
        });

        // add hold and drag event listener for each visible note type hotspot and handle its clone along with it.
        Array.from(hotspots).forEach(hotspot => {

            let that = this;

            // if the hotspot is cloned type, return
            if (hotspot.classList.contains('clonedHotspot')) {
                return;
            }

            // if the hotspot is not of note type, return
            if ((<HTMLElement>hotspot.childNodes[0]).getAttribute('iconType') != 'note') {
                return;
            }

            // prevent opening of content menu on note type icons as context menu and press hold interactions conflict with each other.
            // user would press hold icon to drag them, but on press hold, the icons open context menu, preventing user from being able to drag.
            (<HTMLElement>hotspot).oncontextmenu = function (event) {
                event.preventDefault();
                event.stopPropagation();
                return false;
            };

            // find layer element before hand.
            // needed to search for clone hotspot in this layer.
            let pageDIV = hotspot.getAttribute('pagetype');
            let layerElement = document.querySelector('#ee-overlays-clone' + pageDIV);


            // temporary handling to dynamically set the hold duration. 
            let holdDuration = 50;
            let localValue = +localStorage.getItem('holdDurationValue');
            if (localValue) {
                holdDuration = localValue;
                console.log('hold duration changes to ', holdDuration);
            }

            // add hold event listener and set a flag to mark when hold is active.
            // use this flag to start and end drag.
            interact('#' + hotspot.id).pointerEvents({
                holdDuration: holdDuration,
            }).on('hold', (event) => {
                this.isDragEnabled = !this.disableDrag && true;
            }).draggable({
                listeners: {
                    start(event) {

                        // disable drag of imagebook when dragging hotspot icons
                        if ($("#imgbook").hasClass("dragscroll")) {
                            $("#imgbook").removeClass("dragscroll");
                            dragscroll.reset();
                        }

                        // disable gestures on touch start
                        that.imgBook.enableTouchScreenGestures({
                            disableSwipe: true,
                            disablePan: true,
                            disablePinch: true
                        });
                    },
                    move(event) {

                        // if the flag is set, enable the drag functionality
                        if (that.isDragEnabled) {

                            let oldX = +event.target.getAttribute('oldX') || 0;
                            let oldY = +event.target.getAttribute('oldY') || 0;

                            let newX = +event.dx + oldX;
                            let newY = +event.dy + oldY;

                            event.target.setAttribute('oldX', newX);
                            event.target.setAttribute('oldY', newY);

                            event.target.style.transform = "translate(" + newX + "px, " + newY + "px)";

                            layerElement.querySelector('#clonedHotspot-' + hotspot.id.split('-')[2])['style'].transform = "translate(" + newX + "px, " + newY + "px)";
                        }

                    },
                    // on drag end, unset the flag.
                    // update the note's data to store the new location.
                    end(event) {

                        if (that.isDragEnabled) {

                            that.isDragEnabled = false;



                            event.target.setAttribute('justUpdated', true);

                            setTimeout(() => {
                                event.target.setAttribute('justUpdated', false);
                            }, 10);

                            let oldX = +event.target.getAttribute('oldX') || 0;
                            let oldY = +event.target.getAttribute('oldY') || 0;
                            let noteId = hotspot.getAttribute('note-id');

                            that.notePinPositionUpdateNotifier.next({
                                noteId: noteId,
                                changeX: oldX,
                                changeY: oldY
                            });

                        }

                        // enable the drag and swipe after a small delay so as to not drag / pan for existing drag events.
                        setTimeout(() => {

                            // enable drag of imagebook on hotspot drag end
                            if (!$("#imgbook").hasClass("dragscroll")) {
                                $("#imgbook").addClass("dragscroll");
                                dragscroll.reset();
                            }

                            // enable gestures on touch end
                            that.imgBook.enableTouchScreenGestures({
                                disableSwipe: false,
                                disablePan: false,
                                disablePinch: false
                            });

                        }, 50);


                    }
                },
                restrict: {
                    restriction: '#readerpagediv' + pageDIV,
                    elementRect: { top: 0, left: 0, bottom: 1, right: 1 }
                },
            });


        });


    }

    public removeHotspotEventListeners() {
        let hotspots = document.querySelectorAll('.customVisibleClass');
        
        Array.from(hotspots).forEach(hotspot => {
            hotspot.removeEventListener('mouseenter', this.hotspotEventListener);
        });
    }

    private hotspotEventListener(e) {
        let hotspot = e.target;
        if(hotspot){
            let id = hotspot.id;
            id = id.split('-')[2];
            let pageDiv = hotspot.getAttribute('pagetype');
            let clonedHotspot = document.querySelector('#ee-overlays-clone'+pageDiv).querySelector('#clonedHotspot-'+id);
            clonedHotspot.classList.add('animationClass');
            hotspot.classList.add('animationClass');
            clonedHotspot.classList.add('transitionClass');
            hotspot.classList.add('transitionClass');
            setTimeout(()=> {
                clonedHotspot.classList.remove('animationClass');
                hotspot.classList.remove('animationClass');
            },250);
            setTimeout(()=> {
                clonedHotspot.classList.remove('transitionClass');
                hotspot.classList.remove('transitionClass');
            },500);
        }
    }

    public removeInteractEventListeners() {

        let hotspots = document.querySelectorAll('.customVisibleClass');

        // add hold and drag event listener for each visible note type hotspot and handle its clone along with it.
        Array.from(hotspots).forEach(hotspot => {

            // if the hotspot is cloned type, return
            if (hotspot.classList.contains('clonedHotspot')) {
                return;
            }

            // if the hotspot is not of note type, return
            if ((<HTMLElement>hotspot.childNodes[0]).getAttribute('iconType') != 'note') {
                return;
            }

            interact('#' + hotspot.id).draggable(false);

        });

    }

    /**
     * function to set the disableDrag variable that manages if the drag mode can be enabled or not.
     * @param state - true | false
     */
    setHotspotDragState(state) {
        this.disableDrag = state;
    }

}

enum LIST_VIEW {
    UNITS = "UNITS",
    LESSON = "LESSON"
}

enum PAGE_LAYOUT {
    SINGLE = "single",
    DOUBLE = "double"
}
   